import { faCalendarAlt, faSliders } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';

import { useRouter } from '@boss/hooks';
import { Accordion, SearchDropdown, Select } from '@boss/ui';

import FromToDatePicker from '../../components/Date/FromToDatePicker';

interface Props {
  fromDate?: string;
  toDate?: string;
  eventType?: string;
  eventLocation?: string;
  onSetFilters: (filters: {
    fromDate?: Date | null;
    toDate?: Date | null;
    type?: string;
    location?: string;
    language?: string;
    theme?: string;
    locationId?: string;
  }) => void;
  eventTypes: string[];
  onClearFilters: () => void;
  locations: string[];
  languages: string[];
  language?: string;
  themes: string[];
  theme?: string;
}

const DateFilterOptions = ({ date, label }: { date: string; label: string }) => {
  const { locale } = useRouter();

  return (
    <div>
      <FontAwesomeIcon className="mr-2" icon={faCalendarAlt} />
      <span>{label}: </span>
      <strong>{toLocalizedDate(date, locale)}</strong>
    </div>
  );
};

const toLocalizedDate = (date: string, locale: string) => {
  return new Date(date).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const DateFilters = ({
  fromDate,
  toDate,
  onSetFilters: handleSetFilters,
  eventTypes,
  eventType,
  onClearFilters: handleClearFilters,
  locations,
  eventLocation,
  languages,
  language,
  themes,
  theme,
}: Props) => {
  const { t } = useTranslation('event');

  const showClearButton = fromDate ?? toDate ?? eventType ?? eventLocation ?? language ?? theme;

  const mapLocale = (locale: string) => {
    const normalizedLocale = locale.toLowerCase();

    if (normalizedLocale === 'nl-nl' || normalizedLocale === 'nl-be') {
      return t('filters.languages.nl');
    }

    if (normalizedLocale === 'fr') {
      return t('filters.languages.fr');
    }

    if (normalizedLocale === 'en') {
      return t('filters.languages.en');
    }

    return normalizedLocale;
  };

  const themeOptions = themes.map(theme => {
    if (theme === 'remaining') {
      return { label: t('filters.themeRemaining'), value: 'remaining' };
    }

    return { label: theme, value: theme };
  });

  return (
    <>
      <Accordion
        buttonClassName="py-4 text-blue-dark font-bold justify-end gap-4 font-normal"
        closedIcon={faSliders}
        title={t('filters.title')}
        variant="simple"
      >
        <div className="grid grid-cols-1 gap-4 bg-gray-50 p-4 md:grid-cols-4">
          <FromToDatePicker
            className="pt-0"
            filters={{
              fromDate: fromDate ? new Date(fromDate) : undefined,
              toDate: toDate ? new Date(toDate) : undefined,
            }}
            setFilters={filters =>
              handleSetFilters({
                fromDate: filters.fromDate ? new Date(filters.fromDate) : null,
                toDate: filters.toDate ? new Date(filters.toDate) : null,
              })
            }
            unsetTodayAsMaxDate
          />
          {!!eventTypes?.length && (
            <Select
              className="col-start-1 col-end-2"
              name="event-type"
              onChange={event => handleSetFilters({ type: event.target.value })}
              options={eventTypes.map(type => ({ label: type, value: type }))}
              placeholder={t('filters.type')}
              value={eventType}
            />
          )}
          {!!locations?.length && (
            <SearchDropdown
              dropdownClassName="bg-white w-full border-none"
              onChange={location => handleSetFilters({ location })}
              options={locations.map(location => ({ label: location, value: location }))}
              searchLabel={t('filters.locationSearchLabel') ?? ''}
              selectLabel={t('filters.locationSelectLabel') ?? ''}
              value={eventLocation}
            />
          )}
          {!!languages?.length && (
            <Select
              name="event-language"
              onChange={event => handleSetFilters({ language: event.target.value })}
              options={languages.map(language => ({ label: mapLocale(language), value: language }))}
              placeholder={t('filters.language')}
              value={language}
            />
          )}
          {!!themes?.length && (
            <Select
              name="event-theme"
              onChange={event => handleSetFilters({ theme: event.target.value })}
              options={themeOptions}
              placeholder={t('filters.theme')}
              selectClassName="rounded-50 ring-0"
              value={theme}
            />
          )}
        </div>
      </Accordion>
      {showClearButton && (
        <section className="flex flex-col gap-4 rounded border p-4 shadow-sm md:flex-row">
          <div>{t('filters.appliedFilters')}:</div>
          {fromDate && <DateFilterOptions date={fromDate} label={t('filters.from')} />}
          {toDate && <DateFilterOptions date={toDate} label={t('filters.to')} />}
          {eventType && (
            <div>
              {t('filters.type')}: <strong>{eventType}</strong>
            </div>
          )}
          {eventLocation && (
            <div>
              {t('filters.location')}: <strong>{eventLocation}</strong>
            </div>
          )}
          {language && (
            <div>
              {t('filters.language')}: <strong>{mapLocale(language)}</strong>
            </div>
          )}
          {theme && (
            <div>
              {t('filters.theme')}: <strong>{theme === 'remaining' ? t('filters.themeRemaining') : theme}</strong>
            </div>
          )}
          <div className="flex gap-5 md:ml-auto">
            <button className="flex items-center gap-2 text-[#132D88]" onClick={handleClearFilters}>
              {t('filters.clear')}
            </button>
          </div>
        </section>
      )}
    </>
  );
};

export default DateFilters;
